import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLang, LANGS } from '../i18n/LanguageContext';

const links = [
  { to: '/product', key: 'nav.product' },
  { to: '/how-it-works', key: 'nav.howItWorks' },
  { to: '/integrations', key: 'nav.integrations' },
  { to: '/pricing', key: 'nav.pricing' },
  { to: '/enterprise', key: 'nav.enterprise' },
  { to: '/faq', key: 'nav.faq' },
];

export default function Navbar() {
  const { t, lang, changeLang } = useLang();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 border-b bg-white backdrop-blur-xl ${scrolled ? 'border-[#10282C]/10 shadow-[0_4px_24px_-12px_rgba(16,40,44,0.15)]' : 'border-[#10282C]/8 shadow-[0_2px_16px_-12px_rgba(16,40,44,0.12)]'}`}
      style={{ transition: 'border-color 0.35s ease, box-shadow 0.35s ease' }}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0" aria-label="Gyeno home">
          <img src="/brand/gyeno-lockup-horizontal.svg" alt="Gyeno" className="h-7 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `link-sweep text-[15.5px] font-semibold ${isActive ? 'text-[#00918F]' : 'text-[#10282C]/80 hover:text-[#10282C]'}`
              }
              style={{ transition: 'color 0.25s ease' }}
            >
              {t(l.key)}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="language-switcher"
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-mono font-medium text-[#10282C]/70 hover:bg-[#10282C]/5"
              style={{ transition: 'background-color 0.2s ease' }}
              aria-label="Change language"
            >
              <Globe size={13} /> {lang.toUpperCase()} <ChevronDown size={11} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 mt-2 w-32 rounded-xl bg-white border border-[#10282C]/10 shadow-xl overflow-hidden"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { changeLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3.5 py-2 text-[13px] ${lang === l.code ? 'text-[#00918F] font-semibold bg-[#00C5C2]/8' : 'text-[#10282C]/75 hover:bg-[#10282C]/4'}`}
                    >
                      {l.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/login" data-testid="nav-signin-link" className="link-sweep text-[15.5px] font-semibold text-[#10282C]/70 hover:text-[#10282C]" style={{ transition: 'color 0.2s ease' }}>
            {t('nav.signIn')}
          </Link>
          <Link
            to="/signup"
            data-testid="nav-get-started-btn"
            className="btn-coral px-4.5 py-2 px-5 rounded-full text-[13px] font-semibold"
          >
            {t('nav.getStarted')}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="lg:hidden p-2 text-[#10282C]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white/97 backdrop-blur-xl border-b border-[#10282C]/8 overflow-hidden"
          >
            <div className="px-6 py-5 space-y-3">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className="block text-[15px] font-medium text-[#10282C]/80"
                >
                  {t(l.key)}
                </NavLink>
              ))}
              <div className="flex gap-2 pt-1">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => changeLang(l.code)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-mono ${lang === l.code ? 'bg-[#00918F] text-white' : 'bg-[#10282C]/6 text-[#10282C]/70'}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <div className="pt-2 flex flex-col gap-2.5">
                <Link to="/login" data-testid="mobile-signin-link" onClick={() => setOpen(false)} className="text-[14px] font-medium text-[#10282C]/70">
                  {t('nav.signIn')}
                </Link>
                <Link to="/signup" data-testid="mobile-get-started-btn" onClick={() => setOpen(false)} className="btn-coral inline-flex justify-center px-5 py-2.5 rounded-full text-[14px] font-semibold">
                  {t('nav.getStarted')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
